/**
 * The dense table: every item that matches, sortable by any column, with selection for bulk
 * edits. Keyboard: arrows or j and k move, x or Space selects (Shift extends), Enter opens,
 * Ctrl A selects all, Escape clears the selection.
 *
 * Rows are 40 px with one label chip at most. A column whose value is the same on every row
 * (one owner, all updated just now) is hidden. An item a mission waits on reads "Needs you" (or
 * "Blocked") in Status, the same as the tree and the board.
 *
 * Under 768 px the kind and key columns fold into the title cell, which becomes two lines: the
 * title, then the key (and under 640 px the status) as a caption. The title always keeps its width.
 */
import { Tag as TagIcon, X } from '@orgus/ui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { ITEM_KIND_LABEL, ITEM_PRIORITIES, ITEM_PRIORITY_LABEL, statusLabel } from '@orgus/protocol';
import type { BacklogItem, Id, ItemPriority, ItemStatus, QualityIssue } from '@orgus/protocol';
import { Avatar, Button, Checkbox, ErrorState, Input, RelativeTime, Select, TableSortButton, cn, formatRelative, plural, tableClass, toast, useNow } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { useMissionRows, useMissionsState } from '../missions/data.ts';
import { AttentionChip, attentionOf } from '../missions/MissionBlockerChip.tsx';
import { headlineMission } from '../missions/model.ts';
import { ItemState, KindIcon } from '../missions/parts.tsx';
import { bulkUpdate } from './data.ts';
import { isConstantColumn, ownerHint, ownersDiffer, selectRange, sharedStatuses, sortItems } from './model.ts';
import type { Sort, SortKey } from './model.ts';
import { KeyLabel, PriorityLevel, SeverityMark, Tags } from './parts.tsx';

const PAGE = 100;

interface Col {
  key: SortKey | 'select';
  header: string;
  className?: string;
  align?: 'right';
  sortable?: boolean;
  hide?: string;
}

export function TableView({
  items,
  issues,
  sort,
  onSort,
  selectedId,
  onOpen,
  projectName,
  showProject,
  renderBulk,
}: {
  items: readonly BacklogItem[];
  issues: ReadonlyMap<Id, QualityIssue[]>;
  sort: Sort;
  onSort: (s: Sort) => void;
  selectedId: Id | null;
  onOpen: (id: Id) => void;
  projectName: (id: Id) => string;
  showProject: boolean;
  /** The page's own bulk actions for the picked rows (the shared bottom bar); without it, the table's inline bar. */
  renderBulk?: (picked: BacklogItem[], clear: () => void, selectAll: () => void) => ReactNode;
}) {
  const sorted = useMemo(() => sortItems(items, sort), [items, sort]);
  const [limit, setLimit] = useState(PAGE);
  const shown = sorted.slice(0, limit);
  const order = useMemo(() => shown.map((i) => i.id), [shown]);
  const [selection, setSelection] = useState<Set<Id>>(new Set());
  const [anchor, setAnchor] = useState<Id | null>(null);
  const [active, setActive] = useState<Id | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const anyDefects = items.some((i) => i.kind === 'defect');
  const now = useNow(60_000);
  // The missions on these items: Status says when one waits on a person, as the tree and board do.
  const missionRows = useMissionRows(useMissionsState().state, now);
  // Hide what every visible row shares: it says nothing per row.
  const showOwner = ownersDiffer(shown.map((i) => i.owner));
  const showUpdated = !isConstantColumn(shown.map((i) => formatRelative(i.updatedAt, now)));
  const showPriority = !isConstantColumn(shown.map((i) => i.priority));

  // Selection keeps only what is still on screen.
  useEffect(() => {
    setSelection((s) => {
      const keep = new Set([...s].filter((id) => order.includes(id)));
      return keep.size === s.size ? s : keep;
    });
  }, [order]);
  useEffect(() => {
    if (active) ref.current?.querySelector(`[data-row-id="${CSS.escape(active)}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const select = (id: Id, extend: boolean) => {
    setSelection((s) => selectRange(order, s, anchor, id, extend));
    if (!extend) setAnchor(id);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('input, textarea, select, [role="combobox"]')) return;
    const i = Math.max(0, order.indexOf(active ?? ''));
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      setSelection(new Set(order));
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key === 'ArrowDown' || e.key === 'j') {
      e.preventDefault();
      setActive(order[active ? Math.min(order.length - 1, i + 1) : 0] ?? null);
    } else if (e.key === 'ArrowUp' || e.key === 'k') {
      e.preventDefault();
      setActive(order[Math.max(0, i - 1)] ?? null);
    } else if ((e.key === 'x' || e.key === ' ') && active) {
      e.preventDefault();
      select(active, e.shiftKey);
    } else if (e.key === 'Enter' && active) {
      e.preventDefault();
      onOpen(active);
    } else if (e.key === 'Escape' && selection.size) {
      e.preventDefault();
      e.stopPropagation();
      setSelection(new Set());
    }
  };

  const cols: Col[] = [
    { key: 'select', header: 'Select', className: 'w-10' },
    { key: 'kind', header: 'Kind', className: 'w-10', sortable: true, hide: 'hidden md:table-cell' },
    { key: 'key', header: 'Key', className: 'w-24', sortable: true, hide: 'hidden md:table-cell' },
    { key: 'title', header: 'Title', sortable: true },
    { key: 'status', header: 'Status', className: 'w-32', sortable: true, hide: 'hidden sm:table-cell' },
    ...(showPriority ? [{ key: 'priority' as const, header: 'Priority', className: 'w-24', sortable: true, hide: 'hidden md:table-cell' }] : []),
    ...(anyDefects ? [{ key: 'severity' as const, header: 'Severity', className: 'w-24', sortable: true, hide: 'hidden lg:table-cell' }] : []),
    { key: 'estimate', header: 'Points', className: 'w-16', align: 'right', sortable: true, hide: 'hidden md:table-cell' },
    ...(showOwner ? [{ key: 'owner' as const, header: 'Owner', className: 'w-24', sortable: true, hide: 'hidden sm:table-cell' }] : []),
    ...(showUpdated ? [{ key: 'updated' as const, header: 'Updated', className: 'w-24', align: 'right' as const, sortable: true, hide: 'hidden lg:table-cell' }] : []),
  ];

  const all = order.length > 0 && order.every((id) => selection.has(id));
  const some = !all && order.some((id) => selection.has(id));

  const head = (c: Col): ReactNode => {
    if (c.key === 'select') {
      return <Checkbox aria-label={all ? 'Clear the selection' : 'Select every row'} checked={all} indeterminate={some} onCheckedChange={(on) => setSelection(on ? new Set(order) : new Set())} />;
    }
    if (!c.sortable) return c.header;
    const on = sort.key === c.key;
    return (
      <TableSortButton dir={on ? sort.dir : null} right={c.align === 'right'} onClick={() => onSort({ key: c.key as SortKey, dir: on && sort.dir === 'asc' ? 'desc' : 'asc' })}>
        {c.key === 'kind' ? <span className="sr-only">{c.header}</span> : c.header}
      </TableSortButton>
    );
  };

  const status = (it: BacklogItem, caption: boolean): ReactNode => {
    const m = headlineMission(missionRows, it.id);
    if (m && attentionOf(m.view)) return <AttentionChip view={m.view} short />;
    return <ItemState status={it.status} kind={it.kind} className={caption ? 'min-w-0 gap-1 text-caption' : undefined} />;
  };

  const cell = (c: Col, it: BacklogItem): ReactNode => {
    switch (c.key) {
      case 'select':
        return (
          <Checkbox
            aria-label={`Select ${it.key}`}
            checked={selection.has(it.id)}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(_, e) => select(it.id, (e.nativeEvent as MouseEvent).shiftKey)}
          />
        );
      case 'kind':
        return <KindIcon kind={it.kind} />;
      case 'key':
        return <KeyLabel value={it.key} />;
      case 'title':
        return (
          <span className="flex min-w-0 flex-col">
            {/* The title gives way first; the chip and the project keep their size and the cell clips them, never overlaps. */}
            <span className="flex min-w-0 items-center gap-2 overflow-hidden">
              <span className="min-w-0 truncate text-fg" title={showOwner ? undefined : ownerHint(it.owner)}>
                {it.title}
              </span>
              <Tags values={it.labels} className="hidden md:inline-flex" />
              {showProject && (
                <span className="hidden shrink-0 text-caption text-fg-subtle 2xl:inline" data-meta>
                  {projectName(it.projectId)}
                </span>
              )}
            </span>
            {/* Phone: the folded columns as a caption. */}
            <span className="flex min-w-0 items-center gap-2 md:hidden">
              <KeyLabel value={it.key} />
              <span className="flex min-w-0 sm:hidden">{status(it, true)}</span>
            </span>
          </span>
        );
      case 'status':
        return status(it, false);
      case 'priority':
        return <PriorityLevel priority={it.priority} withLabel />;
      case 'severity':
        return it.defect ? <SeverityMark severity={it.defect.severity} /> : null;
      case 'estimate':
        return it.estimate != null ? <span className="num text-fg-muted">{it.estimate}</span> : null;
      case 'owner':
        return it.owner ? (
          <span className="inline-flex min-w-0 items-center gap-2">
            <Avatar name={it.owner} size={20} decorative />
            <span className="hidden truncate text-caption text-fg-muted xl:inline">{it.owner}</span>
          </span>
        ) : null;
      case 'updated':
        return <RelativeTime ts={it.updatedAt} className="text-caption text-fg-subtle" />;
    }
  };

  const chosen = sorted.filter((i) => selection.has(i.id));
  return (
    // While the shared action bar shows, room under the last row so the bar never hides it.
    <div className={cn('flex flex-col gap-2', renderBulk && chosen.length > 0 && 'pb-16')}>
      {renderBulk ? renderBulk(chosen, () => setSelection(new Set()), () => setSelection(new Set(order))) : chosen.length > 0 && <BulkBar items={chosen} onClear={() => setSelection(new Set())} />}
      <div ref={ref} tabIndex={0} onKeyDown={onKeyDown} className="overflow-auto rounded-card border border-border bg-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus" data-list aria-label="Backlog table">
        <table className="w-full table-fixed border-collapse text-ui">
          <thead className="sticky top-0 z-(--z-sticky) bg-surface">
            <tr className={cn(tableClass.headRow, 'text-left')}>
              {cols.map((c) => (
                <th key={c.key} scope="col" className={cn(tableClass.head, 'px-2 first:pl-4 last:pr-4', c.className, c.hide, c.align === 'right' && 'text-right')} aria-sort={sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
                  {head(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((it) => (
              <tr
                key={it.id}
                data-row-id={it.id}
                data-row
                aria-selected={selection.has(it.id)}
                onClick={() => {
                  setActive(it.id);
                  onOpen(it.id);
                }}
                className={cn(
                  tableClass.rowBase,
                  'cursor-pointer max-md:h-row-2line',
                  selection.has(it.id) ? 'bg-accent-soft' : selectedId === it.id ? tableClass.selected : active === it.id ? 'bg-active' : tableClass.rowHover,
                )}
              >
                {cols.map((c, n) => (
                  <td key={c.key} data-column={c.key} className={cn(tableClass.cell, 'truncate px-2 first:pl-4 last:pr-4', n === 0 && 'relative', c.hide, c.align === 'right' && tableClass.numeric)}>
                    {cell(c, it)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sorted.length > limit && (
        <div className="flex items-center justify-center gap-3 text-ui text-fg-subtle">
          <span className="num">
            Showing {limit} of {sorted.length}
          </span>
          <Button variant="secondary" size="sm" onClick={() => setLimit((l) => l + PAGE)}>
            Show {Math.min(PAGE, sorted.length - limit)} more
          </Button>
        </div>
      )}
    </div>
  );
}

/** The bar that edits every selected item at once. */
function BulkBar({ items, onClear }: { items: BacklogItem[]; onClear: () => void }) {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const statuses = sharedStatuses(items);
  const run = async (patch: Parameters<typeof bulkUpdate>[0]['patch'], what: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await bulkUpdate({ ids: items.map((i) => i.id), patch });
      if (r.skipped.length) toast.success(`${what}: ${r.updated.length} changed, ${r.skipped.length} skipped`, r.skipped[0]!.reason);
      else toast.success(what, plural(r.updated.length, 'item'));
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div role="toolbar" aria-label="Bulk edit" className="flex flex-wrap items-center gap-2 rounded-card border border-accent-line bg-accent-soft px-3 py-1">
      <span className="text-ui font-medium text-fg num">{plural(items.length, 'item')} selected</span>
      <Select
        aria-label="Set status"
        size="sm"
        value=""
        placeholder="Set status"
        disabled={busy || !statuses.length}
        onValueChange={(v) => v && void run({ status: v as ItemStatus }, 'Status changed')}
        options={statuses.filter((s) => s !== 'triage').map((s) => ({ value: s, label: statusLabel(items[0]!.kind === 'defect' && items.every((i) => i.kind === 'defect') ? 'defect' : 'story', s) }))}
        wrapperClassName="w-36"
      />
      <Select
        aria-label="Set priority"
        size="sm"
        value=""
        placeholder="Set priority"
        disabled={busy}
        onValueChange={(v) => v !== '' && void run({ priority: Number(v) as ItemPriority }, 'Priority changed')}
        options={ITEM_PRIORITIES.map((p) => ({ value: String(p), label: ITEM_PRIORITY_LABEL[p] }))}
        wrapperClassName="w-32"
      />
      <form
        className="flex items-center gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          if (!label.trim()) return;
          void run({ addLabels: [label.trim()] }, `Label ${label.trim()} added`).then(() => setLabel(''));
        }}
      >
        <Input aria-label="Add a label" size="sm" icon={<TagIcon />} placeholder="Add label" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40} wrapperClassName="w-32" disabled={busy} />
      </form>
      <Button variant="ghost" size="sm" disabled={busy} onClick={() => void run({ owner: null }, 'Owner cleared')}>
        Clear owner
      </Button>
      <Button variant="ghost" size="sm" icon={<X />} className="ml-auto" onClick={onClear} shortcut={['esc']}>
        Clear selection
      </Button>
      {error && <ErrorState variant="inline" error={error} className="basis-full" />}
    </div>
  );
}
